import type { ReduxApplicationState } from 'src/redux/combinedReducer';
import { selectLowestActiveTaxonomyLabel } from 'src/redux/productsQuery/selectors/selectLowestActiveTaxonomyLabel';
import { getCategoryIdFromCanonicalFilters } from 'src/redux/productsQuery/utils/getCategoryIdFromCanonicalFilters';
import { PageType } from 'src/types/navigation';
import { extractBasePath } from 'src/utils/extractBasePath';

export interface PageTrackingContext {
  entityName?: string;
  pageIdentifier?: string;
  pageType: PageType | null;
}

const getCategoryPageIdentifier = (state: ReduxApplicationState): string | undefined => {
  const id = getCategoryIdFromCanonicalFilters(state.productsQuery.canonicalFilters);
  return id ? String(id) : undefined;
};

const getPathname = (state: ReduxApplicationState): string =>
  state.router?.location?.pathname ||
  (typeof window !== 'undefined' ? window.location.pathname : '');

/**
 * The canonical path of the taxonomy listing, e.g.
 * `/shop/all/home+and+garden/artwork/wall+hangings`.
 *
 * Filter segments, query string and any trailing slash are stripped, so
 * refining or paginating a listing does not change its identity.
 *
 * This is deliberately the page URL rather than an internal ID: it is unique per
 * taxonomy page, stable, readable in a report, and joins directly to the
 * page-level URL data analytics already works from. The previous value was the
 * Vertex attribute key for the page's *depth*, which was identical for every
 * taxonomy page at the same level and so identified nothing. See DISC-4541.
 */
const getTaxonomyPageIdentifier = (state: ReduxApplicationState): string =>
  extractBasePath(getPathname(state));

const getSearchPageIdentifier = (): string => '15000';

const getPartnerPageIdentifier = (state: ReduxApplicationState): string | undefined =>
  state.content.partner?.id ? String(state.content.partner.id) : undefined;

const pageIdentifierExtractors: Partial<
  Record<PageType, (state: ReduxApplicationState) => string | undefined>
> = {
  [PageType.Category]: getCategoryPageIdentifier,
  [PageType.Taxonomy]: getTaxonomyPageIdentifier,
  [PageType.Search]: getSearchPageIdentifier,
  [PageType.Partner]: getPartnerPageIdentifier,
};

const getPageIdentifier = (state: ReduxApplicationState): string | undefined => {
  const pageType = state.navigation.pageType;
  if (!pageType) {
    return undefined;
  }

  const extractor = pageIdentifierExtractors[pageType as PageType];
  return extractor ? extractor(state) : undefined;
};

/**
 * The category's Contentstack heading, e.g. "Gifts For Him". Undefined when the
 * route has no matched content entry, which the content builder represents as an
 * empty string.
 *
 * Sent verbatim rather than case-normalised, matching `trackSEOCopyToggle`, which
 * already labels this page type `category - <heading>`, and matching how the
 * search and partner list names carry their source value.
 */
const getCategoryEntityName = (state: ReduxApplicationState): string | undefined =>
  state.content.category?.heading || undefined;

const getSearchEntityName = (state: ReduxApplicationState): string | undefined =>
  state.productsQuery.searchTerm || undefined;

const getPartnerEntityName = (state: ReduxApplicationState): string | undefined =>
  state.content.partner?.name || undefined;

/**
 * The deepest active taxonomy level's label, e.g. "wall hangings". Undefined at
 * the surface root, which has no listing to name.
 *
 * Gives the taxonomy PLP the same `<pageType> - <entity>` shape that search and
 * partner pages already have. Without it `getFieldObjectList` produced the
 * truncated `"taxonomy - "` on every impression and click.
 */
const getTaxonomyEntityName = (state: ReduxApplicationState): string | undefined =>
  selectLowestActiveTaxonomyLabel(state) || undefined;

const entityNameExtractors: Partial<
  Record<PageType, (state: ReduxApplicationState) => string | undefined>
> = {
  [PageType.Category]: getCategoryEntityName,
  [PageType.Search]: getSearchEntityName,
  [PageType.Partner]: getPartnerEntityName,
  [PageType.Taxonomy]: getTaxonomyEntityName,
};

const getEntityName = (state: ReduxApplicationState): string | undefined => {
  const pageType = state.navigation.pageType;
  if (!pageType) {
    return undefined;
  }

  const extractor = entityNameExtractors[pageType as PageType];
  return extractor ? extractor(state) : undefined;
};

export const getPageTrackingContext = (state: ReduxApplicationState): PageTrackingContext => ({
  pageType: state.navigation.pageType,
  pageIdentifier: getPageIdentifier(state),
  entityName: getEntityName(state),
});
